import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, RotateCcw, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";
import { Switch } from "@/components/ui/switch";
import { CoachLearning } from "@/components/admin/CoachLearning";

export default function AdminCoach() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gp, setGp] = useState("direzione-ripartire");

  useEffect(() => {
    api.get("/admin/coach-config").then(({ data }) => setCfg(data)).catch(() => navigate("/admin", { replace: true }));
  }, [navigate]);

  if (!cfg) return <Loader />;

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/coach-config", next);
      setCfg((c) => ({ ...c, ...data }));
      toast.success("Impostazioni del coach salvate");
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare le regole predefinite?")) return;
    try {
      const { data } = await api.post("/admin/coach-config/reset");
      setCfg((c) => ({ ...c, ...data }));
      toast.success("Regole predefinite ripristinate");
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };

  return (
    <AppShell title="Regole Coach Mino" greeting="Area admin">
      <div className="space-y-5 px-5 pt-6">
        <div className="flex items-center justify-between rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <div>
            <p className="font-semibold text-[#FAF9F6]">Coach attivo</p>
            <p className="text-sm text-[#8A8E96]">{cfg.enabled ? "Gli abbonati possono scrivere al coach" : "Coach in pausa per tutti"}</p>
          </div>
          <Switch
            checked={cfg.enabled}
            disabled={saving}
            onCheckedChange={(v) => save({ enabled: v })}
            data-testid="admin-coach-enabled-switch"
          />
        </div>

        <div className="flex items-center gap-2 text-sm text-[#B4B4BC]" data-testid="admin-coach-safety-count">
          <ShieldCheck size={16} className="text-[#D4AF37]" />
          Interventi di sicurezza negli ultimi 7 giorni: {cfg.safety_events_7d}
        </div>

        <div>
          <p className="mb-2 text-sm text-[#8A8E96]">
            Istruzioni di sistema {cfg.is_default ? "(predefinite)" : "(personalizzate)"}. Profilo e percorso dell'utente vengono aggiunti in automatico.
          </p>
          <textarea
            value={cfg.rules}
            onChange={(e) => setCfg({ ...cfg, rules: e.target.value })}
            rows={18}
            data-testid="admin-coach-rules-textarea"
            className="w-full rounded-2xl border border-[#27272A] bg-[#0D0D0F] p-4 tabular-nums text-[13px] leading-relaxed text-[#D4D4D8] focus:border-[#D4AF37]/60 focus:outline-none"
          />
        </div>

        <div className="flex gap-3" data-testid="admin-coach-rules-actions">
          <button
            onClick={() => save({ enabled: cfg.enabled, rules: cfg.rules })}
            disabled={saving}
            data-testid="admin-coach-save-button"
            className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm uppercase tracking-wide disabled:opacity-50"
          >
            <Save size={16} /> Salva regole
          </button>
          <button
            onClick={reset}
            data-testid="admin-coach-reset-button"
            className="flex items-center justify-center gap-2 rounded-full border border-[#27272A] bg-[#121215] px-4 py-3 text-sm text-[#B4B4BC]"
          >
            <RotateCcw size={16} /> Predefinite
          </button>
        </div>

        <div className="space-y-3 border-t border-[#27272A] pt-5">
          <p className="font-semibold text-[#FAF9F6]">Indicazioni per profilo</p>
          <p className="text-sm text-[#8A8E96]">Tono, focus e cosa evitare per ogni profilo. Coach Mino le usa solo con gli utenti di quel profilo.</p>
          <select
            value={gp}
            onChange={(e) => setGp(e.target.value)}
            data-testid="admin-coach-guide-select"
            className="w-full rounded-xl border border-[#27272A] bg-[#121215] px-3 py-3 text-sm text-[#FAF9F6]"
          >
            {Object.entries(cfg.guide_labels || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <textarea
            value={cfg.guides?.[gp] || ""}
            onChange={(e) => setCfg({ ...cfg, guides: { ...cfg.guides, [gp]: e.target.value } })}
            rows={6}
            data-testid="admin-coach-guide-textarea"
            className="w-full rounded-2xl border border-[#27272A] bg-[#0D0D0F] p-4 text-[14px] leading-relaxed text-[#D4D4D8] focus:border-[#D4AF37]/60 focus:outline-none"
          />
          <button
            onClick={() => save({ enabled: cfg.enabled, guide_profile: gp, guide: cfg.guides?.[gp] || "" })}
            disabled={saving}
            data-testid="admin-coach-guide-save-button"
            className="btn-outline-gold flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm"
          >
            <Save size={16} /> Salva indicazioni del profilo
          </button>
        </div>
        <CoachLearning />
      </div>
    </AppShell>
  );
}
